import { view } from "@risingstack/react-easy-state";
import { Breadcrumbs } from "@tine/designsystem-ui-react";
import isEmpty from "lodash-es/isEmpty";
import isNil from "lodash-es/isNil";
import size from "lodash-es/size";
import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";

import type { CategoryContext, CategoryInfo, CategoryQuery } from "../common/types/categoryTypes";
import type { ProductTileCallbacks } from "../common/types/productTypes";
import { LIST_TYPE } from "../common/types/trackingTypes";
import { stripPossibleHtmlExtension, titleCase } from "../common/utils";
import ProductList from "../components/common/product/ProductList";

import categoryStore from "../stores/categories/categoryStore";
import contentStore from "../stores/cms/contentStore";
import featuresStore from "../stores/features/featuresStore";
import productStore from "../stores/product/productStore";
import searchStore from "../stores/search/searchStore";

import { cn } from "@tine/designsystem-utils";
import { logger } from "../common/logger";

import Button from "../components/Button";
import Loading from "../components/Loading";
import NotFound from "../components/NotFound";
import RichTextRenderer from "../components/cms/RichTextRenderer";

import FilterPanel from "../components/filter/FilterPanel";
import FilterSection from "../components/filter/FilterSection";

import CategoryNavigationVertical from "../components/navigation/CategoryNavigationVertical";
import PageNavigation from "../components/navigation/PageNavigation";
import { prioritizeAgreementAssortment } from "../stores/product/productUtils";
import theme from "../themes/theme";

type ProductListPageProps = {
   cat2?: string;
   cat3?: string;
};

const searchDomains = theme.searchConfig.searchDomains;
const productSearchDomain = searchDomains.find((searchDomain) => searchDomain.type === "product");
const algoliaIndex = productSearchDomain?.indexName || "";

const ProductListPage = ({ cat2, cat3 }: ProductListPageProps) => {
   const [visibleCount, setVisibleCount] = useState<number>(60);

   const cat1 = "produkter";
   const urlKeys: CategoryQuery = { cat1 };
   if (cat2) urlKeys.cat2 = stripPossibleHtmlExtension(cat2);
   if (cat3) urlKeys.cat3 = stripPossibleHtmlExtension(cat3);

   const catKey = `${cat1}-${cat2}-${cat3}`;

   useEffect(() => {
      productStore.clearFilter();
   }, [catKey]);

   let catInfo: CategoryContext | null = null;

   try {
      catInfo = categoryStore.categories.length > 0 ? categoryStore.findCategoryByUrlKey(urlKeys) : null;
   } catch (error) {
      logger.error("products", "Could not find category page.", error, { urlKeys });
   }
   const skus = !isNil(catInfo) && !isNil(catInfo.category) ? catInfo.category.skus : [];
   const variant = catInfo?.category.url_key || "";
   const products = useMemo(() => productStore.resolveSkus(skus), [skus]);
   const sortedProducts = useMemo(
      () => prioritizeAgreementAssortment(products, featuresStore.prioritizeAgreementAssortment),
      [products, featuresStore.prioritizeAgreementAssortment]
   );

   const currentProducts = useMemo(
      () => productStore.filterWithActiveFilters(sortedProducts),
      [sortedProducts, productStore.activeFilters]
   );
   const richText = contentStore.getRichTextForProductCategory(
      `${stripPossibleHtmlExtension(cat2 || "")}${cat3 ? `/${stripPossibleHtmlExtension(cat3)}` : ""}`
   );
   const visibleSlice = currentProducts.slice(0, visibleCount);
   // Used by Algolia to support Personalization, Recommendations, A/B testing and more
   const callbacks: ProductTileCallbacks = {
      click: (product) => {
         searchStore.reportAlgoliaClick(product.sku, algoliaIndex);
      },
      addToCart: (product) => {
         searchStore.reportAlgoliaAddToCart(product.sku, algoliaIndex);
      }
   };

   if (categoryStore.categories.length === 0) {
      return <Loading />;
   }

   if (isNil(catInfo) || (!isNil(catInfo) && isNil(catInfo.category))) {
      return <NotFound />;
   }

   const breadcrumbItems = catInfo.levels.map((c: CategoryInfo) => ({
      label: titleCase(c.name),
      href: c.fullUrl,
      as: Link
   }));

   const isSideMenu = featuresStore.navigationTestVariant === "sidemenu";
   const isTopMenu = featuresStore.navigationTestVariant === "topmenu";
   const isMegaMenu = !isSideMenu && !isTopMenu;

   const productListContainerStyle = cn("tw-flex-auto", { "tw-px-2": isMegaMenu });

   const productListHeader = !isMegaMenu ? titleCase(catInfo.category.name) : catInfo.category.name;

   const rootCategory = categoryStore.categories[0];

   const productCountText = currentProducts.length === 1 ? "1 produkt" : `${currentProducts.length} produkter`;

   const level1CategoryId = catInfo.levels[1]?.id || "";

   const bodyContainerStyle = cn("tw-flex tw-flex-col", {
      "lg:tw-flex-row": isMegaMenu,
      "md:tw-flex-row": isSideMenu
   });
   return (
      <>
         {isMegaMenu && (
            <div className="tw-mb-6">
               <Breadcrumbs items={breadcrumbItems} />
            </div>
         )}
         <div className={bodyContainerStyle}>
            {isMegaMenu && (
               <div className="tw-w-52 tw-shrink-0">
                  <FilterPanel products={products} variant={variant} numberOfProducts={currentProducts.length} />
               </div>
            )}
            {isSideMenu && (
               <div className="tw-w-64 tw-shrink-0 tw-hidden md:tw-block">
                  <CategoryNavigationVertical includeAll categoryInfo={rootCategory} currentCategoryId={level1CategoryId} />
               </div>
            )}

            <div className={productListContainerStyle}>
               <div className="tw-flex tw-justify-between">
                  <h2>{productListHeader}</h2>
                  {!!isMegaMenu && <div className="tw-mb-2 tw-text-nowrap tw-text-sm">{productCountText}</div>}
               </div>
               <nav>
                  <PageNavigation catInfo={catInfo} />

                  {richText && (
                     <div className="product-category-content tw-my-4">
                        <RichTextRenderer document={richText} context="category-content" />
                     </div>
                  )}
                  {!isMegaMenu && <FilterSection products={sortedProducts} categoryKey={catInfo.category.url_key} />}
               </nav>

               {!isMegaMenu && (
                  <h3 className="tw-text-h5">
                     {productListHeader} ({currentProducts.length})
                  </h3>
               )}
               <ProductList
                  products={visibleSlice}
                  callbacks={callbacks}
                  listName={`Kategoriside: ${catInfo.category.name}`}
                  listType={LIST_TYPE.CATEGORY}
                  context="category"
               />
               {isEmpty(visibleSlice) && size(productStore.activeFilters) > 1 && (
                  <p className="tw-mt-1">
                     Vi fant dessverre ingen produkter som matcher dine valg i filteret. Prøv igjen med et annet valg.
                  </p>
               )}
               {currentProducts.length > visibleCount && (
                  <div className="tw-text-center">
                     <Button
                        variant="tertiary"
                        className="tw-my-6 [&>svg]:tw-rotate-90"
                        onClick={() => setVisibleCount(visibleCount + 42)}
                     >
                        Vis flere produkter
                     </Button>
                  </div>
               )}
            </div>
         </div>
      </>
   );
};

export default view(ProductListPage);
